import { FunctionComponent, useState, useEffect } from 'react';
import { Layout } from 'components/Layout/Layout';
// @ts-ignore
import Head from 'next/head';
import styled, { css } from 'styled-components';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useMediaQuery } from 'react-responsive';
import { Consts as StyleConsts } from 'styles/consts';
import { Accordion } from 'components/Section/Accordion';
import { useRouter } from 'next/router';
import { FlexWrapper, FlexBox } from 'components/Layout/Flex';
import { Image } from 'components/Layout/Image';
import { Spacer } from 'components/Layout/Spacer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLongArrowAltRight } from '@fortawesome/free-solid-svg-icons';
import { competitionCords, evaluationCriteriaSubmission } from 'assets/content';
import { JUDGES } from 'consts';
import { Footer } from 'components/Layout/Footer';
import { scrollTop } from 'utils';
import { Header } from '../components/Layout/Header';
import { ActionButton } from '../components/Buttons/ActionButton';
import { ColorDefs } from '../styles/colorDefs';
import Link from 'next/link';

const SubmissionExpand: FunctionComponent<{
  item: any;
  index: number;
  submissionOpenedIndex: number;
  openSubmissionIndex: any;
}> = ({ item, index, submissionOpenedIndex, openSubmissionIndex }) => {
  return (
    <SubmissionExpandWrapper opened={index === submissionOpenedIndex}>
      <p>{item.name}</p>
      <div>
        <Text>{item.text}</Text>
      </div>
      <ActionButton
        onClick={() =>
          index === submissionOpenedIndex
            ? openSubmissionIndex(-1)
            : openSubmissionIndex(index)
        }
        isSmall
        background="black"
        color="#4CCDD1"
      >
        {index === submissionOpenedIndex ? '-' : '+'}
      </ActionButton>
    </SubmissionExpandWrapper>
  );
};

const FinalistsExpand: FunctionComponent<{
  item: any;
  index: number;
  finalistsOpenedIndex: number;
  openFinalistsIndex: any;
  isMobile: boolean;
  children: any;
}> = ({
  item,
  children,
  index,
  finalistsOpenedIndex,
  openFinalistsIndex,
  isMobile,
}) => {
  return (
    <FinalistsExpandWrapper opened={index === finalistsOpenedIndex}>
      {isMobile ? (
        <>
          <div>
            <p>{item.percent}%</p>
            <p>{item.name}</p>
          </div>
          <ActionButton
            onClick={() =>
              index === finalistsOpenedIndex
                ? openFinalistsIndex(-1)
                : openFinalistsIndex(index)
            }
            isSmall
            background="black"
            color="#B8DDD6"
          >
            {index === finalistsOpenedIndex ? '-' : '+'}
          </ActionButton>
          <div>{children}</div>
        </>
      ) : (
        <>
          <p>{item.percent}%</p>
          <FontAwesomeIcon icon={faLongArrowAltRight as IconProp} />
          <div>
            <div>
              <p>{item.name}</p>
              <ActionButton
                onClick={() =>
                  index === finalistsOpenedIndex
                    ? openFinalistsIndex(-1)
                    : openFinalistsIndex(index)
                }
                isSmall
                background="black"
                color="#B8DDD6"
              >
                {index === finalistsOpenedIndex ? '-' : '+'}
              </ActionButton>
            </div>
            <div>{children}</div>
          </div>
        </>
      )}
    </FinalistsExpandWrapper>
  );
};

const Competition: FunctionComponent = () => {
  const router = useRouter();
  const [timelineOpenedIndex, openTimelineIndex] = useState<number | string>(
    -1,
  );
  const [submissionOpenedIndex, openSubmissionIndex] = useState(-1);
  const [finalistsOpenedIndex, openFinalistsIndex] = useState(-1);
  const [selectedAdjudication, selectAdjudication] = useState(JUDGES[0].name);
  const [isMobile, setMobile] = useState(false);

  const mobile = useMediaQuery({
    query: `(max-width: ${StyleConsts.mobileBreakPoint})`,
  });
  useEffect(() => {
    setMobile(mobile);
  }, [mobile]);

  const onRoute = (path) => {
    scrollTop();
    router.push(path);
  };

  const selectedMember = JUDGES.filter(
    (member) => member.name === selectedAdjudication,
  )[0];

  return (
    <Layout>
      <Head>
        <title>The Earth Prize - The Competition</title>
        <meta
          name="description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:title" content="The Earth Prize - The Competition" />
        <meta
          property="og:description"
          content="The Earth Prize is the environmental sustainability competition for teenage students. The Earth Prize will recognize the students and schools with the best solutions to accelerate positive change towards environmental sustainability, as judged by The Earth Prize Adjudicating Panel."
        />
        <meta property="og:image" content="/images/cover.jpg" />
      </Head>
      <Header />
      <FlexWrapper>
        <FlexBox background="#78494D" color="#F4F4F4F4" justify="flex-end">
          <Spacer height={70} />
          <H1>The Competition</H1>
          <Spacer height={10} />
          <Text color="#EDA06D">
            The Earth Prize is a $100,000 environmental sustainability
            competition open to teenage students worldwide. It recognizes and
            awards students who develop the best solutions to accelerate
            positive change towards environmental sustainability.
          </Text>
          {!mobile && <Spacer height={40} />}
        </FlexBox>
        <FlexBoxStyled background="#78494D" color="#EDA06D" withoutPadding>
          <Image image="/images/laying.webp" position="center" />
        </FlexBoxStyled>
      </FlexWrapper>
      <FlexWrapper id="basics">
        <FlexBox background="#CD6E62" color="#F4F4F4F4">
          <H1>THE BASICS</H1>
          <Spacer height={40} />
          <H2>HOW TO APPLY</H2>
          <Spacer height={20} />
          <Text color="#F4F4F4">
            Registration is open from September
            <br />
            1st until November 30th.
          </Text>
          <Spacer height={40} />
          <Text color="#F4F4F4">
            Fill out the online registration form to get access to My Earth
            Prize Platform, where you will have access to our exclusive content
            and mentoring
          </Text>
          <Spacer height={30} />
          {/* <StepsWrapper>
            <div>
              <div />
              <div />
            </div>
            <p>
              Fill out the online registration form to get access to My Earth
              Prize Platform, where you will have access to our exclusive
              content and mentoring
            </p>
            <div>
              <div />
              <div />
            </div>
            <p>
              Make sure that your Supervisor or Team Lead (who could be you, if
              you are a student) registers and creates your team on the
              Submissions Portal.
            </p>
            <div>
              <div />
            </div>
            <p>Complete the necessary team information and get thinking!</p>
          </StepsWrapper> */}
          <Spacer height={10} />
          {/*<Link href="/registration">*/}
          {/*  <DarkButton>REGISTER</DarkButton>*/}
          {/*</Link>*/}
          <Spacer height={15} />
          <Link href="/rules">
            <LightButton>Read the full rules of the competition</LightButton>
          </Link>
          <Eligibility>
            <H2>ELIGIBILITY</H2>
            <Text>
              Participants must have been born between 2005 and 2011 and be
              registered in a school or educational program.
            </Text>
          </Eligibility>
        </FlexBox>
        <FlexBox background="#CD6E62" color="#F4F4F4F4" withoutPadding>
          <Image image="/images/wheat.webp" position="center" />
        </FlexBox>
      </FlexWrapper>
      <AccordionsWrapper id="timeline">
        <Padding justify="flex-end">
          <Spacer height={50} />
          <H1>THE TIMELINE</H1>
          {competitionCords(onRoute).map((cord, cordI) => (
            <div key={cord.title}>
              <Timeline color={cord.color}>
                <H2>{cord.title}</H2>
                <div />
                <H2>{cord.date}</H2>
              </Timeline>
              {cord.tabs.map((tab, i) => (
                <GrowWrapper background={cord.color} z={i * 2}>
                  <APadding>
                    <Accordion
                      label={tab.name}
                      color="black"
                      index={timelineOpenedIndex}
                      setIndex={openTimelineIndex}
                      direction="row-reverse"
                      justify="space-between"
                      breaker={false}
                      disabled={false}
                      header
                      i={`${cordI}-${i}`}
                    >
                      <>
                        {tab.content}
                        {tab.action && (
                          <Link href={tab.action.path}>
                            <Action background="black" color={cord.color}>
                              {tab.action.label}
                            </Action>
                          </Link>
                        )}
                      </>
                    </Accordion>
                  </APadding>
                  {cord.tabs.length - 1 !== i && (
                    <Cap background={cord.color} z={i * 2 + 1} />
                  )}
                </GrowWrapper>
              ))}
            </div>
          ))}
          <FAQsWrapper>
            <Text color="white">Do you still have questions?</Text>
            <Link href="/faq">
              <LightButton color="black">
                Read our FAQs&nbsp;&nbsp;&nbsp;
                <FontAwesomeIcon icon={faLongArrowAltRight as IconProp} />
              </LightButton>
            </Link>
          </FAQsWrapper>
        </Padding>
      </AccordionsWrapper>
      <FlexWrapper isfullPage id="evaluation-criteria">
        <EvaluationWrapper>
          <Padding justify="flex-start">
            <Spacer height={50} />
            <H1>EVALUATION CRITERIA</H1>
            <Spacer height={50} />
            <Phase color="#467D86">
              <H2>REGISTRATION PHASE</H2>
              <Spacer height={30} />
              <Text>
                All teams that have successfully completed their registration on
                our website will automatically enter the Submissions Phase of
                the competition.
              </Text>
            </Phase>
            <Phase color="#4CCDD1">
              <H2>SUBMISSION PHASE</H2>
              <Spacer height={30} />
              <Text>
                At the Submissions Phase, the Fresh Idea will be evaluated
                according to the following criteria:
              </Text>
              <Spacer height={30} />
              {evaluationCriteriaSubmission.map((item, index) => (
                <SubmissionExpand
                  key={item.name}
                  item={item}
                  index={index}
                  submissionOpenedIndex={submissionOpenedIndex}
                  openSubmissionIndex={openSubmissionIndex}
                />
              ))}
            </Phase>
            <Phase color="#B8DDD6">
              <H2>Regional Winners Phase</H2>
              <Spacer height={30} />
              <Text>
                At the Regional Winners Phase, the Scholars&apos; submissions
                will be evaluated by the Adjudicating Panel to select the seven
                (7) Regional Winners. Each of the two (2) submissions will be
                evaluated using specific criteria and weighting.
              </Text>
              <Spacer height={50} />
              <FinalistsWrapper>
                <div>
                  <FinalistsExpand
                    finalistsOpenedIndex={finalistsOpenedIndex}
                    index={0}
                    isMobile={isMobile}
                    openFinalistsIndex={openFinalistsIndex}
                    item={{ name: 'Final idea', percent: 80 }}
                  >
                    <p>
                      The Adjudicating Panel’s evaluation of the pitch video
                      will taking into account the following aspects:
                    </p>
                    <ul>
                      <li>Applicability</li>
                      <li>Innovativeness</li>
                      <li>Inspirational Impact</li>
                      <li>Relevance</li>
                      <li>Scalability</li>
                    </ul>
                  </FinalistsExpand>
                </div>
                <div>
                  <FinalistsExpand
                    finalistsOpenedIndex={finalistsOpenedIndex}
                    index={3}
                    isMobile={isMobile}
                    openFinalistsIndex={openFinalistsIndex}
                    item={{
                      name: 'The Pitch Video',
                      percent: 20,
                    }}
                  >
                    <p>
                      The Adjudicating Panel’s evaluation of the pitch video
                      will taking into account the following aspects:
                    </p>
                    <ul>
                      <li>
                        How clearly the video explains the problem, solution,
                        and impact of their Idea
                      </li>
                      <li>
                        How logically the information presented in the video
                        flows
                      </li>
                      <li>How well the video draws in the audience</li>
                    </ul>
                  </FinalistsExpand>
                </div>
              </FinalistsWrapper>
            </Phase>
            <Spacer height={50} />
          </Padding>
        </EvaluationWrapper>
        <AdjudicationWrapper id="adjudicating-panel">
          <H1>THE ADJUDICATING PANEL</H1>
          {isMobile ? (
            <div>
              <AdjudicationInfoWrapper>
                <AdjudicationPicture opened image={selectedMember.photo} />
                <p>{selectedMember.name}</p>
                <p>{selectedMember.position}</p>
                <div>
                  {selectedMember.paragraphs.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                </div>
              </AdjudicationInfoWrapper>
              <AdjudicationPicturesWrapper>
                {JUDGES.filter(
                  (member) => member.name !== selectedMember.name,
                ).map((member) => (
                  <a href="#adjudicating-panel">
                    <AdjudicationPicture
                      opened={member.name === selectedMember.name}
                      image={member.photo}
                      onClick={() => selectAdjudication(member.name)}
                    />
                  </a>
                ))}
              </AdjudicationPicturesWrapper>
              <Quote />
            </div>
          ) : (
            <div>
              <AdjudicationPicturesWrapper>
                {JUDGES.map((member) => (
                  <AdjudicationPicture
                    opened={member.name === selectedMember.name}
                    image={member.photo}
                    onClick={() => selectAdjudication(member.name)}
                  />
                ))}
              </AdjudicationPicturesWrapper>
              <AdjudicationInfoWrapper>
                <p>{selectedMember.name}</p>
                <p>{selectedMember.position}</p>
                <div>
                  {selectedMember.paragraphs.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                </div>
                <Quote />
              </AdjudicationInfoWrapper>
            </div>
          )}
          <CountMeInWrapper>
            <H3>Count me in</H3>
            <Text color="white">
              Registrations for The Earth Prize 2024 have now closed.
              Pre-register now for the next year&apos;s edition.
            </Text>
            <Spacer height={isMobile ? 40 : 70} />
            {/*<Link href="/registration">*/}
            {/*  <LightButton color="black" bgcolor="#21B7BB">*/}
            {/*    REGISTER*/}
            {/*  </LightButton>*/}
            {/*</Link>*/}
          </CountMeInWrapper>
        </AdjudicationWrapper>
        <InspirationWrapper>
          <H1>NEED SOME INSPIRATION?</H1>
          <Spacer height={40} />
          <TextBold color="white">
            Meet The Earth Prize 2024 Winners
            <br />
            and Runners-up
          </TextBold>
          <Spacer height={isMobile ? 40 : 70} />
          <Link href="/the-earth-prize-2024">
            <LightButton color="#21B7BB" bgcolor="black">
              LEARN MORE
            </LightButton>
          </Link>
        </InspirationWrapper>
      </FlexWrapper>
      <Footer fullHeight onTopClick={() => scrollTop()} />
    </Layout>
  );
};

interface ITheme {
  background: string;
  color: string;
}
interface IPadding {
  justify: string;
}
interface IBackground {
  background: string;
  z: number;
}

const Padding = styled.div<IPadding>`
  ${({ justify, theme: { Consts } }) => css`
    flex-grow: 1;
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: ${justify};
    max-width: 1450px;
    @media (min-width: ${Consts.smallTabletBreakPoint}) {
      padding: 40px 120px 40px 40px;
    }
    @media (min-width: ${Consts.tabletBreakPoint}) {
      padding: 50px 160px 50px 50px;
    }
  `}
`;

const FlexBoxStyled = styled(FlexBox)`
  position: initial;
`;

const DarkButton = styled.button<{ bgcolor?: string; color?: string }>`
  ${({ theme: { Consts }, bgcolor, color }) => css`
    padding: 28px;
    background: ${bgcolor || '#724B4E'};
    color: ${color || 'white'};
    font-weight: bold;
    font-family: ${Consts.defaultFontFamily};
    width: fit-content;
    font-size: 18px;
    border-radius: 50px 50px 50px 3px;
    border: 0;
    cursor: pointer;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 12px;
      height: 40px;
      min-width: 90px;
      padding: 0 9px;
    }
  `}
`;

const LightButton = styled.button<{ bgcolor?: string; color?: string }>`
  ${({ theme: { Consts }, bgcolor, color }) => css`
    padding: 28px;
    background: ${bgcolor || 'white'};
    font-family: ${Consts.defaultFontFamily};
    color: ${color || '#C17366'};
    font-weight: bold;
    font-size: 18px;
    border-radius: 3px 50px 50px 50px;
    width: fit-content;
    border: 0;
    cursor: pointer;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 12px;
      height: 40px;
      min-width: 90px;
      padding: 0 9px;
    }
  `}
`;

const StepsWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: grid;
    grid-template-columns: 7px 1fr;
    grid-gap: 0 30px;
    align-items: stretch;
    padding-left: 20px;
    max-width: 410px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      width: 75%;
      font-size: 11px;
      bottom: auto;
      top: calc(50% - 60px);
      left: 15%;
    }

    > p {
      font-weight: 400;
      font-size: 16px;
      padding-bottom: 40px;
      position: relative;
      top: -8px;
    }

    > div {
      display: grid;
      grid-template-rows: 7px 1fr;
      align-items: center;

      > div {
        &:nth-child(1) {
          height: 7px;
          width: 7px;
          border-radius: 50%;
          background-color: #724b4e;
        }
        &:nth-child(2) {
          width: 1px;
          margin: 0 auto;
          background-color: #724b4e;
          height: 100%;
        }
      }
    }
  `}
`;

const AccordionsWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 100%;
    background: black;
    overflow: hidden;
    padding-bottom: 30px;

    a {
      cursor: pointer;
    }
    // Makes extra bold selected accordions headers
    > div {
      > div {
        &:wrappercontentd(3),
        &:nth-child(9),
        &:nth-child(13),
        &:nth-child(16) {
          > div {
            &:nth-child(2) {
              > div {
                &:nth-child(1) {
                  > div {
                    &:nth-child(1) {
                      > div {
                        &:nth-child(1) {
                          > div {
                            &:nth-child(3) {
                              font-size: 26px;
                              text-transform: uppercase;

                              @media (max-width: ${Consts.tabletBreakPoint}) {
                                font-size: 20px;
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `}
`;

const FAQsWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    align-items: center;
    gap: 50px;
    margin-top: 80px;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      flex-direction: column;
      align-items: flex-start;
      gap: 30px;
    }
  `}
`;

const Timeline = styled.div<{ color: string }>`
  ${({ color, theme: { Consts } }) => css`
    display: flex;
    align-items: center;
    gap: 40px;
    width: 100%;
    margin-bottom: 40px;
    margin-top: 70px;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      flex-direction: column;
      align-items: flex-start;
      gap: 15px;
      margin-bottom: 28px;
      margin-top: 50px;
    }

    > h2 {
      &:nth-child(1) {
        color: ${color};
      }
      &:nth-child(3) {
        color: white;
        text-transform: none;
      }
    }

    > div {
      height: 1px;
      flex-grow: 1;
      background-color: ${color};

      @media (max-width: ${Consts.smallTabletBreakPoint}) {
        display: none;
      }
    }
  `}
`;

const Cap = styled.div<IBackground>`
  ${({ background, z, theme: { Consts } }) => css`
    width: 100%;
    height: 50px;
    background: ${background};
    position: absolute;
    bottom: -49px;
    z-index: ${z};
    @media (min-width: ${Consts.mobileBreakPoint}) {
    }
  `}
`;

const CountMeInWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    align-items: center;
    text-align: center;
    flex-direction: column;
    max-width: 800px;
    margin: 90px auto 0;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      padding: 0 20px;
      margin: 40px auto 0;
    }
  `}
`;

const AdjudicationWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 100%;
    height: 100%;
    background-color: ${ColorDefs.neutralBlack};
    color: white;
    padding: 100px 0;

    > h1 {
      padding: 0 50px;
    }

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      padding: 50px 0;

      > h1 {
        padding: 0 20px;
      }
    }
  `}
`;

const AdjudicationPicturesWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    display: flex;
    align-items: flex-start;
    width: 100%;
    height: 330px;
    margin-top: 50px;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      height: 230px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      height: auto;
      display: grid;
      grid-template-columns: 1fr 1fr;
    }
  `}
`;

const AdjudicationPicture = styled.div<{ opened: boolean; image: string }>`
  ${({ theme: { Consts }, opened, image }) => css`
    background-image: ${`url(${image})`};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: all 0.2s;
    width: 20%;
    cursor: pointer;
    height: ${opened ? '330px' : '300px'};

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      height: ${opened ? '230px' : '200px'};
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      height: 200px;
      width: 100%;
    }
  `}
`;

const AdjudicationInfoWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    padding: 0 50px;
    width: 100%;
    max-width: 850px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin: 80px auto 0;
    text-align: center;

    > p {
      &:nth-child(2) {
        font-size: 25px;
        font-weight: 900;
        margin-bottom: 15px;

        @media (max-width: ${Consts.mobileBreakPoint}) {
          margin-top: 60px;
          font-size: 18px;
        }
      }

      &:nth-child(3) {
        font-size: 25px;
        font-weight: 400;
        margin-bottom: 50px;

        @media (max-width: ${Consts.mobileBreakPoint}) {
          font-size: 18px;
        }
      }
    }

    > div {
      p {
        margin-bottom: 15px;
        font-size: 16px;
        line-height: 22px;

        @media (max-width: ${Consts.mobileBreakPoint}) {
          padding: 0 40px;
        }
      }
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 0;
      margin: 50px auto 0;

      > div {
        &:nth-child(1) {
          height: 350px;
          background-position: center 30%;
        }
      }
    }
  `}
`;

const Quote = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 103px;
    height: 116px;
    margin: 80px auto 0;
    background-image: url('/images/quotes-adjudication.png');
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      margin: 50px auto 0;
    }
  `}
`;

const InspirationWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 100%;
    height: 100%;
    background-color: ${ColorDefs.robinsEggBlue};
    color: white;
    padding: 100px 50px;
    display: flex;
    align-items: center;
    text-align: center;
    flex-direction: column;
    justify-content: center;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      padding: 50px;
    }
  `}
`;

const EvaluationWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 100%;
    height: 100%;
    background-color: white;
    color: black;

    @media (min-width: ${Consts.mobileBreakPoint}) {
    }
  `}
`;

const Phase = styled.div<{ color: string }>`
  ${({ theme: { Consts }, color }) => css`
    width: 100%;
    padding: 50px;
    border-radius: 20px 20px 0 0;
    margin-bottom: -20px;
    background-color: ${color};

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      padding: 50px 30px;
    }
  `}
`;

const Eligibility = styled.div`
  ${({ theme: { Consts } }) => css`
    position: absolute;
    top: 20%;
    width: 436px;
    right: -390px;
    background: #ffffff;
    border-radius: 70px 200px 200px 3px;
    padding: 50px;
    z-index: 2;

    > h2 {
      color: #c17366;
      margin-bottom: 20px;
    }

    > p {
      color: #c17366;
    }

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      position: relative;
      right: auto;
      width: 100%;
      top: auto;
      margin: 50px 0 -140px 0;
      padding: 30px;
    }
  `}
`;

const FinalistsWrapper = styled.div`
  ${({ theme: { Consts } }) => css`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: stretch;
    grid-gap: 40px;

    > div {
      padding-top: 20px;
      display: flex;
      flex-direction: column;
      gap: 30px;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      grid-template-columns: 1fr;
      grid-gap: 0;

      > div {
        gap: 20px;
        padding-top: 0;

        &:nth-child(2) {
          display: none;
        }
      }
    }
  `}
`;

const SubmissionExpandWrapper = styled.div<{ opened: boolean }>`
  ${({ theme: { Consts }, opened }) => css`
    width: 100%;
    display: flex;
    align-items: start;
    gap: 20px;
    margin-bottom: 20px;
    max-width: 900px;

    @media (max-width: ${Consts.smallTabletBreakPoint}) {
      flex-wrap: wrap;
      justify-content: space-between;
      margin: 0;
    }

    > p {
      font-weight: 700;
      font-size: 20px;
      margin: 0;
      position: relative;
      top: 5px;
      white-space: nowrap;

      @media (max-width: ${Consts.smallTabletBreakPoint}) {
        font-size: 16px;
        white-space: normal;
      }
    }

    > div {
      border-top: 1px solid black;
      margin-top: 12px;
      max-height: ${opened ? '5000px' : '0px'};
      overflow: hidden;
      flex-grow: 1;
      padding: ${opened ? '20px 0 10px' : '0'};
      font-size: 16px;
      position: relative;
      top: 7px;

      @media (max-width: ${Consts.smallTabletBreakPoint}) {
        order: 3;
        width: 100%;
        flex-shrink: 0;
        margin: 0;
        border: 0;
        padding: ${opened ? '0 0 30px' : '0'};
      }
    }

    > button {
      margin-top: 3px;
      width: 40px;
      height: 30px;
      font-size: 16px;
      min-width: 0;
      border-radius: 10px;
      border-bottom-left-radius: 3px;

      @media (max-width: ${Consts.tabletBreakPoint}) {
        width: 26px;
        height: 18px;
        font-size: 12px;
      }
    }
  `}
`;

const FinalistsExpandWrapper = styled.div<{ opened: boolean }>`
  ${({ theme: { Consts }, opened }) => css`
    width: 100%;
    display: flex;
    align-items: start;
    gap: 20px;
    max-width: 490px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      justify-content: space-between;
      flex-wrap: wrap;
      align-items: center;

      > div {
        &:nth-child(1) {
          max-width: 200px;
          > p {
            font-weight: 700;
            font-size: 18px;
          }
        }

        &:nth-child(3) {
          width: 100%;
          flex-shrink: 0;
          height: ${opened ? 'auto' : '0px'};
          margin-top: ${opened ? '20px' : '0px'};
          padding-bottom: ${opened ? '40px' : '0px'};
          overflow: hidden;
          margin: 0;

          > p {
            font-size: 14px;
            margin: 0 0 10px;
          }

          > ul {
            font-size: 14px;
            padding-left: 20px;

            > li {
              margin-bottom: 5px;
            }
          }
        }
      }
    }

    > p {
      font-weight: 700;
      font-size: 20px;
      margin: 0;
      position: relative;
      top: 5px;
      white-space: nowrap;
    }

    > svg {
      position: relative;
      top: 7px;
    }

    > div {
      flex-grow: 1;

      > div {
        &:nth-child(1) {
          display: flex;
          align-items: flex-end;
          gap: 30px;

          > p {
            font-weight: 700;
            font-size: 20px;
            margin: 0;
          }

          > button {
            margin-top: 3px;
            width: 40px;
            height: 30px;
            font-size: 16px;
            min-width: 0;
            border-radius: 10px;
            border-bottom-left-radius: 3px;

            @media (max-width: ${Consts.tabletBreakPoint}) {
              width: 26px;
              height: 18px;
              font-size: 12px;
            }
          }
        }

        &:nth-child(2) {
          height: ${opened ? 'auto' : '0px'};
          margin-top: ${opened ? '20px' : '0px'};
          overflow: hidden;
          padding-right: 30px;
          > p {
            font-size: 16px;
            margin: 0 0 10px;
          }

          > ul {
            font-size: 16px;
            padding-left: 20px;

            > li {
              margin-bottom: 5px;
            }
          }
        }
      }
    }
  `}
`;

const GrowWrapper = styled.div<IBackground>`
  ${({ background, z, theme: { Consts } }) => css`
    width: 100%;
    background: ${background};
    border-radius: 20px 20px 0 0;
    border-top: 1px solid black;
    border-left: 1px solid black;
    border-right: 1px solid black;
    border-bottom: 0 solid transparent;
    z-index: ${z};
    position: relative;
    @media (min-width: ${Consts.mobileBreakPoint}) {
    }
  `}
`;

const APadding = styled.div`
  padding: 10px 20px;
`;

const H1 = styled.h1`
  ${({ theme: { Consts } }) => css`
    font-size: 50px;
    text-transform: uppercase;
    font-weight: bold;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 28px;
    }
  `}
`;

const H2 = styled.h2`
  ${({ theme: { Consts } }) => css`
    font-size: 26px;
    text-transform: uppercase;
    font-weight: bold;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 20px;
    }
  `}
`;

const H3 = styled.h2`
  ${({ theme: { Consts } }) => css`
    font-weight: 900;
    font-size: 40px;
    line-height: 55px;
    margin: 0 0 40px;

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 25px;
      line-height: 34px;
    }
  `}
`;

interface IText {
  color?: string;
  size?: string;
}

const Text = styled.p<IText>`
  ${({ color = 'black', size, theme: { Consts } }) => css`
    font-size: ${size || '18px'};
    color: ${color};

    a {
      color: ${color};
      text-decoration: underline;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 14px;
    }
  `}
`;

const TextBold = styled.p<IText>`
  ${({ color = 'black', size, theme: { Consts } }) => css`
    font-size: ${size || '25px'};
    font-weight: bold;
    color: ${color};

    a {
      color: ${color};
      text-decoration: underline;
    }

    @media (max-width: ${Consts.mobileBreakPoint}) {
      font-size: 14px;
    }
  `}
`;

const Action = styled.div<ITheme>`
  ${({ background, color }) => css`
    width: fit-content;
    height: 60px;
    border-radius: 30px 30px 30px 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: bold;
    font-size: 14px;
    text-transform: uppercase;
    background: ${background};
    color: ${color};
    margin: 20px 0 0 0;
    padding: 0 20px;
    cursor: pointer;
  `}
`;

export default Competition;
